// CreditControlSettings.tsx
import React, { useState, useEffect } from "react";
import CreditLimitSection from "./CreditLimitSection";
import BlockingRulesSection from "./BlockingRulesSection";
import NotificationSection from "./NotificationSection";
import ReminderSection from "./ReminderSection";
import { CreditLimitData, BlockingRulesData, WhatsAppData, ReminderData } from "./types";
import { getCreditControlSettings, updateCreditControlSettings } from "src/shared/api";
import { notifyError, notifySuccess } from "src/assets/toastService";
import { useUser } from "src/business-types/grocery/components/UserContext";
import { errorHandling } from "src/shared/services/errorHandling";

export default function CreditControlSettings() {
  const { setBlockingRules: setBR, setCreditLimit: setCL, setWhatsapp: setWP, setReminder: setRM } = useUser();
  const [creditLimit, setCreditLimit] = useState<CreditLimitData>({
    defaultLimit: 1000,
    maxLimit: 5000,
    allowNewCustomers: true,
    lowBalanceAlert: 80,
  });

  const [blockingRules, setBlockingRules] = useState<BlockingRulesData>({
    blockOnExceed: true,
    allowDraftOrders: true,
    requireApproval: true,
  });

  const [whatsapp, setWhatsapp] = useState<WhatsAppData>({
    enabled: true,
    number: "",
    language: "English",
    sendOrderReady: true,
    sendPaymentReminder: true,
  });

  const [reminder, setReminder] = useState<ReminderData>({
    enabled: true,
    days: "1, 3, 7, 15",
    time: "11:00 AM",
  });

  const [saving, setSaving] = useState(false);

  const fetchCreditControlSettings = () => {
    getCreditControlSettings().then((data) => {
      setCL(data.creditLimit);
      setBR(data.blockingRules);
      setWP(data.whatsapp);
      setRM(data.reminder);
      setCreditLimit(data.creditLimit);
      setBlockingRules(data.blockingRules);
      setWhatsapp(data.whatsapp);
      setReminder(data.reminder);
    })
      .catch((error) => {
        errorHandling(error);
      });
  }

  useEffect(() => {
    fetchCreditControlSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateCreditControlSettings({ creditLimit, blockingRules, whatsapp, reminder });
      fetchCreditControlSettings(); // Refresh settings after save
      notifySuccess("Settings saved!");
    } catch (err) {
      notifyError("Failed to save settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        <h1 className="text-lg font-semibold text-gray-900">Credit Control Settings</h1>

        <CreditLimitSection data={creditLimit} onChange={setCreditLimit} />
        <BlockingRulesSection data={blockingRules} onChange={setBlockingRules} />
        <NotificationSection data={whatsapp} onChange={setWhatsapp} />
        <ReminderSection data={reminder} onChange={setReminder} />

        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-500 text-white text-sm font-medium px-5 py-2.5 rounded-lg hover:bg-blue-600 disabled:opacity-60"
        >
          {saving ? "Saving..." : "Save Settings"}
        </button>
      </div>
    </div>
  );
}